import React, {useMemo} from 'react';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {captureMessage} from 'web-app/services/sentry';
import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {type Warnable, dispatchWarnings, showWarningsListSummary} from 'web-app/util/warnable';

import {type AutomaticPricing} from '../profile/child-profile/plans-and-invoices/ad-hoc-purchases/hooks/use-get-automatic-pricing-for-purchases';
import {Modal} from './components/modal';
import {StyledLoadingOverlay} from './components/styled-loading-overlay';
import {useHandlers} from './hooks/use-handlers';
import {usePermissions} from './hooks/use-permissions';
import {usePricingGroupsForAdhocPurchase} from './hooks/use-pricing-group-for-adhoc-purchase';
import {useSharedSelectOptions} from './hooks/use-select-options';
import {useSimplifiedUsBillingPlanData} from './hooks/use-simplified-us-billing-plan-data';
import {useSimplifiedUsBillingPlanState} from './hooks/use-simplified-us-billing-plan-state';
import {useSubmission} from './hooks/use-submission';
import {useSubmitNewItem} from './hooks/use-submit-new-item';
import {SimplifiedUSBillingPlanModal} from './simplified-us-billing-plan-modal';
import * as Tracking from './tracking';
import type * as Types from './types';

type Props = {
    onClose: VoidFunction;
    afterSubmission?: Types.AfterSubmission;
    handleAddPlanTemplatesModalClose?: VoidFunction;
    handleLegacyPlanModalOpen: VoidFunction;
    initiallySelectedChildIds: NonEmptyArray<string>;
    allChildren: Array<Types.Child>;
    siteId: string;
    automaticRuleGroupId: string | null;
    automaticPricings: Array<AutomaticPricing>;
    selectedTemplate: Types.Template | null;
    subView: Types.CreateSubView;
};

const Loader = ({
    onClose,
    afterSubmission,
    initiallySelectedChildIds,
    allChildren,
    siteId,
    automaticRuleGroupId,
    automaticPricings,
    selectedTemplate,
    handleAddPlanTemplatesModalClose,
    handleLegacyPlanModalOpen,
    subView,
}: Props) => {
    const simplifiedUsBillingPlan = dispatchWarningsToSentry(
        'Issues with useSimplifiedUsBillingPlanData',
        useSimplifiedUsBillingPlanData({
            siteId,
            initialTuitions: selectedTemplate ? selectedTemplate.tuitions.map(tuition => tuition.id) : [],
            initialFees: selectedTemplate ? selectedTemplate.fees : [],
            customDiscounts: [],
            startDate: null,
        }),
    );
    const {billingFrequencies, billingProfiles, isAutomaticDecouplingEnabled, automaticInvoicing, isLoading} = useMemo(
        () => simplifiedUsBillingPlan,
        [simplifiedUsBillingPlan],
    );

    const {canAddNewItem, canOverridePrice, canAddNewDiscount} = usePermissions(siteId);

    const {state, dispatch} = useSimplifiedUsBillingPlanState({
        simplifiedUsBillingPlan,
        initiallySelectedChildIds,
        allChildren,
        view: 'create',
        subView,
        initialPlan: selectedTemplate,
    });
    const {tuitionOptions, feeOptions, discountOptions, childrenOptions, billingFrequencyOptions, pricingGroupOptions} =
        useSharedSelectOptions({
            state,
            permissions: {
                canAddNewItem,
                canAddNewDiscount,
                isAutomaticDecouplingEnabled,
            },
            pricingGroups: simplifiedUsBillingPlan.pricingGroups,
        });

    const {
        childrenState: {selectedChildren},
        tuitionsState: {selectedTuitions},
        feesState: {selectedFees},
        discountState: {allDiscounts},
        billingFrequencyState: {selectedBillingFrequency},
        billingPeriodState: {selectedBillingPeriod},
        pricingGroupState: {selectedPricingGroup},
        customPeriodState: {customPeriodDates},
        endDateState: {selectedEndDate},
        displayedModal,
        template: {shouldAddNewTemplate},
    } = useMemo(() => state, [state]);

    const handlers = useHandlers({
        dispatch,
        state,
        billingFrequencies,
        pricingGroups: simplifiedUsBillingPlan.pricingGroups,
    });

    const {
        handleChildrenChange,
        handleModalToggle,
        handleModalClose,
        handleToggleMoreOptions,
        handleBillingFrequencyChange,
        handlePricingGroupChange,
        handleBillingPeriodChange,
        handleDiscountChange,
        handleEndDateChange,
        handleCustomEndPeriodProratedItemUpdate,
        handleCustomEndPeriodSave,
        handleCustomEndPeriodCancel,
        handleAllTuitionsUpdate,
        handleAllFeesUpdate,
        toggleTuitionFieldAtIndex,
        handleTuitionsChange,
        appendTuitionField,
        removeTuitionField,
        toggleFeeFieldAtIndex,
        handleFeesChange,
        appendFeeField,
        removeFeeField,
        handleCustomStartPeriodChange,
        handleCustomStartPeriodProratedItemUpdate,
        handleCustomStartPeriodSave,
        setUpEmptyPlan,
        openAddNewTemplateModal,
        handleAddNewTemplateChange,
        handleUpdatePlanValidationErrors,
        handleRemoveChildrenWithError,
        setUpBillingFrequency,
    } = handlers;

    const adhocPurchasePricingGroups = usePricingGroupsForAdhocPurchase({
        selectedChildIds: selectedChildren.map(child => child.id),
        selectedPricingGroupId: selectedPricingGroup,
        customPeriodStartDate: customPeriodDates?.startDate ?? null,
        customPeriodEndDate: selectedEndDate,
        automaticRuleGroupId,
        automaticPricings,
    });

    const {handleSubmit, handleSavePlan, isSavePlanDisabled, isSubmissionLoading} = useSubmission({
        trackSubmission:
            hasValue(selectedBillingFrequency) && hasValue(selectedBillingPeriod)
                ? () =>
                      Tracking.newPlanSaved({
                          childrenAmount: selectedChildren.length,
                          tuition: selectedTuitions.length,
                          fee: selectedFees.length,
                          automaticBilling:
                              selectedBillingFrequency.type === 'none'
                                  ? 'do not automate'
                                  : selectedBillingFrequency.type,
                          firstBillingPeriod:
                              selectedBillingPeriod.type === 'custom'
                                  ? false
                                  : selectedBillingPeriod.startDate.numericFormat,
                          discount: allDiscounts.length,
                          endDate: Boolean(selectedEndDate),
                          planTemplateSaved: shouldAddNewTemplate,
                      })
                : null,
        afterSubmission: (updatedChildIds: Array<Types.Child['id']>) => {
            onClose();
            afterSubmission?.(updatedChildIds);
            handleAddPlanTemplatesModalClose?.();
        },
        setUpBillingFrequency,
        setUpEmptyPlan,
        state,
        siteId,
        billingProfiles,
        pricingGroups: pricingGroupOptions,
        adhocPurchasePricingGroups,
        openAddNewTemplateModal: shouldAddNewTemplate ? openAddNewTemplateModal : undefined,
        handleUpdatePlanValidationErrors,
        automaticRuleGroupId,
    });

    const submitNewItem = useSubmitNewItem({
        billingProfiles: simplifiedUsBillingPlan.billingProfiles,
        handleAllTuitionsUpdate,
        handleAllFeesUpdate,
        handleModalClose,
        pricingGroupState: state.pricingGroupState,
        displayedModal,
        siteId,
        allChildren,
    });

    return (
        <StyledLoadingOverlay loading={isLoading}>
            <Modal
                siteId={siteId}
                state={state}
                handlers={handlers}
                pricingGroupOptions={pricingGroupOptions}
                automaticInvoicing={automaticInvoicing}
                submission={{
                    handleSubmit,
                    handleSavePlan,
                    isSubmissionLoading,
                    isSavePlanDisabled,
                }}
                submitNewItem={submitNewItem}
            />
            <SimplifiedUSBillingPlanModal
                onClose={onClose}
                saveButton={{
                    onSave: handleSavePlan,
                    isDisabled: isSavePlanDisabled,
                    isLoading: isSubmissionLoading,
                }}
                cancelButton={{
                    onCancel: () => {
                        onClose();
                        Tracking.newPlanModalCanceled();
                    },
                }}
                childrenOptions={childrenOptions}
                feeOptions={feeOptions}
                tuitionOptions={tuitionOptions}
                pricingGroupOptions={pricingGroupOptions}
                discountOptions={discountOptions}
                billingFrequencyOptions={billingFrequencyOptions}
                handleBillingFrequencyChange={handleBillingFrequencyChange}
                handleDiscountChange={handleDiscountChange}
                state={state}
                handleToggleMoreOptions={handleToggleMoreOptions}
                handlePricingGroupChange={handlePricingGroupChange}
                handleEndDateChange={handleEndDateChange}
                handleBillingPeriodChange={handleBillingPeriodChange}
                handleCustomEndPeriodProratedItemUpdate={handleCustomEndPeriodProratedItemUpdate}
                handleCustomEndPeriodSave={handleCustomEndPeriodSave}
                handleCustomEndPeriodCancel={handleCustomEndPeriodCancel}
                handleChildrenChange={handleChildrenChange}
                handleTuitionsChange={handleTuitionsChange}
                appendTuitionField={appendTuitionField}
                removeTuitionField={removeTuitionField}
                toggleTuitionFieldAtIndex={toggleTuitionFieldAtIndex}
                toggleFeeFieldAtIndex={toggleFeeFieldAtIndex}
                handleFeesChange={handleFeesChange}
                appendFeeField={appendFeeField}
                removeFeeField={removeFeeField}
                handleCustomStartPeriodChange={handleCustomStartPeriodChange}
                handleCustomStartPeriodProratedItemUpdate={handleCustomStartPeriodProratedItemUpdate}
                handleCustomStartPeriodSave={handleCustomStartPeriodSave}
                handleModalToggle={handleModalToggle}
                handleModalClose={handleModalClose}
                canOverrideTuitionPrice={canOverridePrice}
                billingFrequencies={billingFrequencies}
                handleAddNewTemplateChange={handleAddNewTemplateChange}
                handleRemoveChildrenWithError={handleRemoveChildrenWithError}
                handleLegacyPlanModalOpen={handleLegacyPlanModalOpen}
            />
        </StyledLoadingOverlay>
    );
};

const dispatchWarningsToSentry = <TV, TW>(title: string, warnable: Warnable<TV, TW>): TV =>
    dispatchWarnings(warnable, warnings => captureMessage(`${title}: ${showWarningsListSummary(warnings)}`));

export {Loader as SimplifiedUSBillingPlanModal};
